/**
 * agentCatalog — single source of truth for all 13 agents' marketing
 * copy. The landing pages, the "Prenajmi si AI agenta" funnel, and the
 * comparison tables all read from here. Adding a new agent = one
 * object in this array.
 *
 * Tone: matches Mika's Telegram "Profitron Bot" reference — direct
 * Slovak, emotional address ("Ty", "Tvoje"), 👉 CTA closers, urgency
 * but not slimy. Numbers (€, hours saved) come from realistic Phase-1
 * benchmarks; flagged `realistic` so the FE never overhypes.
 */

export const AGENTS = [
  {
    slug: "review-reply",
    name: "Review Reply Agent",
    tagline: "Odpovie na každú recenziu skôr, než tvoj konkurent zistí, že existuje.",
    emoji: "💬",
    accent: "#10B981",
    price_eur_month: 79,
    saves: {
      time_hours_per_week: 8,
      money_eur_per_month: 640,
      energy: "Žiaden viac stres z 1-hviezdičkových review v sobotu večer.",
    },
    does_for_you: [
      "Monitoruje Google, Heureka, Trustpilot, Facebook 24/7",
      "Píše empathic odpovede v tvojom brand voice (SK / CZ / EN / DE)",
      "Eskaluje 1★ reviews na email v ten istý moment",
      "Generuje týždenný digest top sťažností pre teba",
    ],
    vs_manual: {
      manual: "8h/týždeň ručného odpovedania, často s 24-48h oneskorením",
      with_agent: "0h/týždeň, odpoveď do 15 minút (priemer)",
      saves_pct: 100,
    },
    pain_quote: "Tvoj 1★ review je vidieť 1 247× než stihneš odpovedať.",
    cta_emotional: "Nečakaj na sobotu. Agent ti chráni meno aj keď spíš.",
    demo_url: "/agent-os/demo/review-reply",
  },
  {
    slug: "helpdesk",
    name: "Helpdesk Agent",
    tagline: "Mailbox plný otázok? Agent ti odpíše skôr, než stihneš otvoriť Gmail.",
    emoji: "📧",
    accent: "#3B82F6",
    price_eur_month: 99,
    saves: {
      time_hours_per_week: 15,
      money_eur_per_month: 1200,
      energy: "Žiaden viac nedeľný večer pri inboxe.",
    },
    does_for_you: [
      "Číta inbox cez Gmail OAuth (live integrácia)",
      "Generuje drafts odpovedí (ty potvrdíš jedným klikom)",
      "Kategorizuje: refund / shipping / pre-sale / spam",
      "Auto-eskaluje urgentné prípady",
    ],
    vs_manual: {
      manual: "15h/týždeň písania rovnakých odpovedí",
      with_agent: "1h/týždeň schvaľovania draftov",
      saves_pct: 93,
    },
    pain_quote: "Priemerný e-shop majiteľ trávi 730h ročne v inboxe. To je 91 pracovných dní.",
    cta_emotional: "Pošli inbox agentovi. Vráť si víkendy.",
    demo_url: "/agent-os/demo/helpdesk",
  },
  {
    slug: "cart-recovery",
    name: "Cart Recovery Agent",
    tagline: "Každý opustený košík je peniaze, ktoré ti niekto iný odnesie.",
    emoji: "🛒",
    accent: "#F59E0B",
    price_eur_month: 89,
    saves: {
      time_hours_per_week: 0,
      money_eur_per_month: 3200,
      energy: "Žiaden viac panika pri pohľade na 'abandoned cart' štatistiky.",
    },
    does_for_you: [
      "Sleduje opustené košíky v real-time",
      "Posiela 3-stage email sekvenciu (60min / 24h / 72h)",
      "Personalizuje subject line podľa hodnoty košíka",
      "A/B testuje varianty automaticky",
    ],
    vs_manual: {
      manual: "Strata 67% opustených košíkov navždy",
      with_agent: "Recovery 23-31% (priemer EU benchmark)",
      saves_pct: 75,
    },
    pain_quote: "Z 1000€ obratu mesačne ti utečie €670 v opustených košíkoch. Bez agenta.",
    cta_emotional: "Tieto peniaze už zarobil. Stačí ich vrátiť domov.",
    demo_url: "/agent-os/demo/cart-recovery",
  },
  {
    slug: "product-desc",
    name: "Product Description Agent",
    tagline: "1000 produktov, 1 deň. SEO-friendly, brand voice, 10 jazykov.",
    emoji: "✍️",
    accent: "#8B5CF6",
    price_eur_month: 89,
    saves: {
      time_hours_per_week: 20,
      money_eur_per_month: 1600,
      energy: "Žiaden viac copywriter za €25/hodinu.",
    },
    does_for_you: [
      "Píše produktové popisy z bullet pointov alebo feed-u",
      "SEO meta titles + descriptions",
      "Bulk-genere v 10 jazykoch súčasne",
      "Brand voice consistency cez celý katalóg",
    ],
    vs_manual: {
      manual: "Copywriter €25/h × 5min/produkt = €2.10/produkt × 1000 = €2100",
      with_agent: "€89/mes pre unlimited produktov + 10 jazykov",
      saves_pct: 96,
    },
    pain_quote: "Tvoj copywriter dnes napíše 50 popisov. Agent ich napíše 1000. Za rovnakú cenu.",
    cta_emotional: "Tvoja konkurencia už spustila 500 nových produktov tento mesiac. Ty?",
    demo_url: "/agent-os/demo/product-desc",
  },
  {
    slug: "newsletter",
    name: "Newsletter Agent",
    tagline: "Týždenný newsletter, ktorý sa otvára. Bez Canva, bez bloku, bez termínu.",
    emoji: "📰",
    accent: "#EC4899",
    price_eur_month: 79,
    saves: {
      time_hours_per_week: 6,
      money_eur_per_month: 480,
      energy: "Žiaden viac 'čo dnes napíšem' v utorok večer.",
    },
    does_for_you: [
      "Generuje 4 newsletter drafts dopredu na mesiac",
      "Personalizuje podľa segmentov (VIP / new / churned)",
      "Subject line A/B test (3 varianty)",
      "Integruje sa s Resend / Mailchimp / Brevo",
    ],
    vs_manual: {
      manual: "6h/týždeň písania + dizajn + scheduling",
      with_agent: "30min/mes schvaľovania",
      saves_pct: 90,
    },
    pain_quote: "Tvoj newsletter má 18% open rate. Konkurencia 41%. Rozdiel je AI personalizácia.",
    cta_emotional: "Newsletter, ktorý sa otvára. Aj keď ty spíš.",
    demo_url: "/agent-os/demo/newsletter",
  },
  {
    slug: "gdpr-watch",
    name: "GDPR Watch Agent",
    tagline: "Spí, kým úrad nezavolá. Potom vie všetko, čo treba.",
    emoji: "🛡️",
    accent: "#EF4444",
    price_eur_month: 49,
    saves: {
      time_hours_per_week: 2,
      money_eur_per_month: 20000,   // potential GDPR fine avoided
      energy: "Spánok bez nočných môr o úradoch.",
    },
    does_for_you: [
      "Monitor cookie banner compliance týždenne",
      "Audit DPA podpisov s tretími stranami",
      "Email alert keď príde GDPR sťažnosť",
      "Generuje DPIA dokumenty na vyžiadanie",
    ],
    vs_manual: {
      manual: "Právnik €150/h × 4h/mes = €600",
      with_agent: "€49/mes + alert 24/7",
      saves_pct: 92,
    },
    pain_quote: "Priemerná GDPR pokuta v EU je €175 000. Tvoj agent stojí €49/mes.",
    cta_emotional: "€49 dnes alebo €175 000 zajtra. Tvoj výber.",
    demo_url: "/agent-os/demo/gdpr-watch",
  },
  {
    slug: "social-media",
    name: "Social Media Agent",
    tagline: "FB + IG + TikTok obsah na 30 dní za 5 minút.",
    emoji: "📱",
    accent: "#06B6D4",
    price_eur_month: 99,
    saves: {
      time_hours_per_week: 12,
      money_eur_per_month: 1500,
      energy: "Žiaden viac 'čo dnes postnúť'.",
    },
    does_for_you: [
      "Generuje 30-day content kalendár",
      "Píše caption-y v brand voice",
      "Navrhuje hashtagy + best posting times",
      "Re-mixuje top performers",
    ],
    vs_manual: {
      manual: "Social media manager €1500/mes",
      with_agent: "€99/mes + ty schvaľuješ",
      saves_pct: 93,
    },
    pain_quote: "Tvoja konkurencia postuje 5x týždenne. Ty 1x. Algoritmus si pamätá.",
    cta_emotional: "Vráť sa do hry. Bez full-time social media manažéra.",
    demo_url: "/agent-os/demo/social-media",
  },
  {
    slug: "pricing-intel",
    name: "Pricing Intel Agent",
    tagline: "Vie ceny tvojej konkurencie skôr ako oni sami.",
    emoji: "💰",
    accent: "#F59E0B",
    price_eur_month: 129,
    saves: {
      time_hours_per_week: 5,
      money_eur_per_month: 4500,    // margin protection
      energy: "Žiaden viac strach, že podsekávaš.",
    },
    does_for_you: [
      "Scraping cien konkurencie 3x denne",
      "Alert keď podlieziš o >15%",
      "Návrh dynamického ceníka",
      "Margin protection rules",
    ],
    vs_manual: {
      manual: "Pricing analyst €3000/mes part-time",
      with_agent: "€129/mes + auto-alerts",
      saves_pct: 96,
    },
    pain_quote: "Predávaš za €19. Konkurencia dnes znížila na €17. Vieš to už?",
    cta_emotional: "Tvoja marža sa stráca, kým toto čítaš.",
    demo_url: "/agent-os/demo/pricing-intel",
  },
  {
    slug: "customer-onboarding",
    name: "Customer Onboarding Agent",
    tagline: "Nový zákazník = lifetime customer. Ak vie, ako tvoj produkt používať.",
    emoji: "🎯",
    accent: "#10B981",
    price_eur_month: 79,
    saves: {
      time_hours_per_week: 4,
      money_eur_per_month: 600,
      energy: "Menej refundov, viac retencie.",
    },
    does_for_you: [
      "Personalizovaná welcome sekvencia (5 emailov)",
      "Onboarding video skripty",
      "Activation tracking + nudges",
      "Churn-risk alerts",
    ],
    vs_manual: {
      manual: "Activation rate 23%",
      with_agent: "Activation rate 47% (priemer EU SaaS)",
      saves_pct: 50,
    },
    pain_quote: "75% nových zákazníkov nikdy znova nenakúpi. Onboarding to mení.",
    cta_emotional: "Spravme z prvého nákupu lifetime vzťah.",
    demo_url: "/agent-os/demo/customer-onboarding",
  },
  {
    slug: "review-moderator",
    name: "Review Moderator Agent",
    tagline: "Fake reviews, hate, troll-y. Agent ich vyhodí skôr, než sa ich dotkneš.",
    emoji: "🚦",
    accent: "#8B5CF6",
    price_eur_month: 49,
    saves: {
      time_hours_per_week: 3,
      money_eur_per_month: 240,
      energy: "Žiaden viac trauma z hate komentárov.",
    },
    does_for_you: [
      "Auto-flag fake reviews (AI fraud detection)",
      "Klasifikuje hate / spam / constructive",
      "Generuje response do queue",
      "Týždenný moderation report",
    ],
    vs_manual: {
      manual: "Moderátor €600/mes",
      with_agent: "€49/mes + 99.2% accuracy",
      saves_pct: 92,
    },
    pain_quote: "Jeden fake 1★ review ti zoberie €1240 obratu (research, eMarketer 2024).",
    cta_emotional: "Vlastnenie konverzácie = vlastnenie značky.",
    demo_url: "/agent-os/demo/review-moderator",
  },
  {
    slug: "behavior-analytics",
    name: "Behavior Analytics Agent",
    tagline: "Vie, prečo ti zákazník odišiel bez nákupu. Aj vie, čo s tým.",
    emoji: "📊",
    accent: "#06B6D4",
    price_eur_month: 99,
    saves: {
      time_hours_per_week: 6,
      money_eur_per_month: 2800,
      energy: "Žiaden viac dohad. Reálne dáta, real-time akcie.",
    },
    does_for_you: [
      "Track session recordings + heatmaps",
      "AI insights 'prečo tu padá konverzia'",
      "Auto-suggesty na A/B testy",
      "Weekly digest top friction points",
    ],
    vs_manual: {
      manual: "Hotjar €99 + analyst čítanie sám",
      with_agent: "€99/mes + AI ti to vysvetlí ako 7-ročnému",
      saves_pct: 70,
    },
    pain_quote: "97% návštevníkov odíde bez nákupu. Vieš prečo? Agent áno.",
    cta_emotional: "Prestaň hádať. Začni vedieť.",
    demo_url: "/agent-os/demo/behavior-analytics",
  },
  {
    slug: "product-importer",
    name: "Product Importer Agent",
    tagline: "Heureka feed → tvoj WooCommerce. Bez copy-paste, bez chýb, denne.",
    emoji: "📦",
    accent: "#EF4444",
    price_eur_month: 89,
    saves: {
      time_hours_per_week: 10,
      money_eur_per_month: 800,
      energy: "Žiaden viac manuálne kopírovanie SKU.",
    },
    does_for_you: [
      "Parse XML / CSV / Google Shopping feed",
      "Push do WooCommerce / Shopify / PrestaShop / Magento / OpenCart",
      "Idempotent (žiadne duplikáty)",
      "Daily auto-sync 03:00 UTC",
    ],
    vs_manual: {
      manual: "10h/týždeň copy-paste produktov",
      with_agent: "0h, beží automaticky",
      saves_pct: 100,
    },
    pain_quote: "Dodávateľ ti pošle 500 nových SKU. Začínaš v pondelok ráno?",
    cta_emotional: "Spi v pondelok. Produkty sú už v shope.",
    demo_url: "/agent-os/demo/product-importer",
  },
  {
    slug: "sales-hunter",
    name: "Agent Obchodník",
    tagline: "Fractional CEO za €99/mes. Nájde diery, zlaté prasiatka, funnely.",
    emoji: "🎯",
    accent: "#10B981",
    price_eur_month: 99,
    saves: {
      time_hours_per_week: 8,
      money_eur_per_month: 4000,
      energy: "Strategický rhythm bez full-time CEO za €8000/mes.",
    },
    does_for_you: [
      "Identifikuje diery na trhu v tvojej nike",
      "Navrhuje 'zlaté prasiatka' (high-margin niky)",
      "Píše funnel ideas (hook → step1 → step2 → step3 → channel)",
      "Audit existujúcich tovární s konkrétnymi fixmi",
    ],
    vs_manual: {
      manual: "Growth consultant €150/h × 4h/mes = €600 + 2-týždňové delivery",
      with_agent: "€99/mes + instant brief",
      saves_pct: 83,
    },
    pain_quote: "Tvoja konkurencia má rast 12% MoM. Ty? Bez plánu žiadny rast.",
    cta_emotional: "Strategický plán. Dnes. Nie keď bude 'čas'.",
    demo_url: "/agent-os/demo/sales-hunter",
  },
  {
    slug: "competitive-manager",
    name: "Agent Manager",
    tagline: "Porovnáva konkurenciu, nájde slabiny, posiela akčný plán. Musíme byť #1.",
    emoji: "👑",
    accent: "#F59E0B",
    price_eur_month: 99,
    saves: {
      time_hours_per_week: 6,
      money_eur_per_month: 3500,
      energy: "Žiaden viac 'oni majú lepšie ceny / shipping / SEO'.",
    },
    does_for_you: [
      "Porovnávacia matica per-konkurent (silné/slabé)",
      "Surfaceuje weak spots ('kde udrieť')",
      "5 priority-sorted akčných krokov na overtake",
      "Strategický exec summary 1× týždenne",
    ],
    vs_manual: {
      manual: "Competitive analyst €4000/mes + 2 týždne na briefing",
      with_agent: "€99/mes + instant briefing",
      saves_pct: 97,
    },
    pain_quote: "Konkurencia ti dnes spustila novú kampaň. Tvoja reakcia o 3 týždne?",
    cta_emotional: "Predbehnúť, alebo byť predbehnutý. Iné možnosti nie sú.",
    demo_url: "/agent-os/demo/competitive-manager",
  },

  // V218.8 — SEO Indexer Agent (LIVE, 16th agent)
  {
    slug: "seo-indexer",
    name: "SEO Indexer Agent",
    tagline: "Nový blog post o 10 minút v Bingu, Yandexe a Seznam.cz. Bez prosenia o crawl.",
    emoji: "🔎",
    accent: "#06B6D4",
    price_eur_month: 49,
    saves: {
      time_hours_per_week: 3,
      money_eur_per_month: 240,
      energy: "Koniec čakania 6 týždňov kým Bing objaví tvoj nový produkt.",
    },
    does_for_you: [
      "Každú hodinu sleduje tvoj sitemap.xml",
      "Detekuje nové URL od posledného behu (diff, nie spam)",
      "Pushne do Bing, Yandex, Seznam.cz, Naver cez IndexNow protokol",
      "Pingne Google sitemap endpoint pre rýchlejší re-crawl",
      "Anomaly alert ak je sitemap rozbitý alebo engines down",
    ],
    vs_manual: {
      manual: "Submit každého URL ručne v 4 webmaster tools = 15 min/post × 8 postov/týždeň = 2h",
      with_agent: "0 minút, plne autonómne, idex do hodiny od publish",
      saves_pct: 100,
    },
    pain_quote: "Tvoj nový blog post sa do Google dostane za 4 týždne. Pretek si už prehral.",
    cta_emotional: "AI vyhľadávače crawlujú denne. Pomalý sitemap = stratený organic traffic.",
    demo_url: "/agent-os/demo/seo-indexer",
  },

  // ─────────────────────────────────────────────────────────────
  // V143 — "Pripravujeme & testujeme pre teba" — 12 ďalších agentov.
  // Tieto agenti sú v aktívnom vývoji / testovaní s pilot klientmi.
  // Sú zobrazení vo funneli s "🔬 Pripravujeme" badge, nemajú demo
  // sandbox URL ani "rent" CTA — namiesto toho "🔔 Daj mi vedieť"
  // čakaciu listinu.
  // ─────────────────────────────────────────────────────────────
  {
    slug: "invoice-ocr",
    name: "Invoice OCR Agent",
    tagline: "PDF, foto, sken — agent prečíta každú faktúru a pošle do Pohody za 8 sekúnd.",
    emoji: "🧾",
    accent: "#8B5CF6",
    price_eur_month: 89,
    coming_soon: true,
    eta: "Q3 2026",
    saves: {
      time_hours_per_week: 5,
      money_eur_per_month: 600,
      energy: "Žiadne viac ručné prepisovanie cez Pohodu o 23:00.",
    },
    does_for_you: [
      "AI OCR z PDF / fotky / scanu (presnosť 98.4%)",
      "Auto-push do Pohody / iDokladu / Money S3 / Stormware",
      "Detekcia duplikátov + alert pri podozrivej DPH",
      "Mesačný report 'kde tečú peniaze'",
    ],
    vs_manual: {
      manual: "Účtovník €25/h × 20 faktúr × 5min = €83/mes",
      with_agent: "€89/mes + 0 chýb",
      saves_pct: 92,
    },
    pain_quote: "Za rok prepíšeš 1200 faktúr ručne. Tvoj agent ich prečíta v jeden poobedie.",
    cta_emotional: "Tvoj účtovník ti poďakuje. Tvoja peňaženka tiež.",
    demo_url: null,
  },
  {
    slug: "meeting-notes",
    name: "Meeting Notes Agent",
    tagline: "Zoom/Teams nahrávka → transcript + action items v Slacku za 4 minúty.",
    emoji: "📝",
    accent: "#06B6D4",
    price_eur_month: 69,
    coming_soon: true,
    eta: "Q3 2026",
    saves: {
      time_hours_per_week: 4,
      money_eur_per_month: 480,
      energy: "Žiadne viac 'kto čo mal urobiť po meetingu?'",
    },
    does_for_you: [
      "Auto-transcript Zoom/Teams/Meet/Loom (SK/CZ/EN/DE)",
      "Extrahuje action items + assignee + deadline",
      "Push do Slack/Notion/Linear/Trello",
      "Týždenný recap 'open action items'",
    ],
    vs_manual: {
      manual: "Junior assistant €1200/mes alebo 4h týždenne tvoja",
      with_agent: "€69/mes + nikto nezabudne",
      saves_pct: 95,
    },
    pain_quote: "Po každom meetingu zabudneš 60% rozhodnutí do 24h. Tvoj agent nezabudne nikdy.",
    cta_emotional: "Meetingy končia. Akcie začínajú.",
    demo_url: null,
  },
  {
    slug: "email-triage",
    name: "Email Triage Agent",
    tagline: "Ranný 90-second digest: prečítam ti inbox a poviem, čo je dôležité.",
    emoji: "📬",
    accent: "#3B82F6",
    price_eur_month: 59,
    coming_soon: true,
    eta: "Q3 2026",
    saves: {
      time_hours_per_week: 7,
      money_eur_per_month: 560,
      energy: "Žiadny inbox-anxiety, žiadne 200 unread.",
    },
    does_for_you: [
      "Gmail / Outlook OAuth (read-only)",
      "Ranný digest: 5 priorít + 10 to-skim + zvyšok auto-archive",
      "Detekuje VIP / customer / fakturácia / spam",
      "Stručné AI summary každého threadu",
    ],
    vs_manual: {
      manual: "1h denne na inbox skenovanie",
      with_agent: "5min ranný digest, focus na zvyšok dňa",
      saves_pct: 88,
    },
    pain_quote: "Otvoríš inbox o 8:00. Zatvoríš o 10:30. Práca ešte nezačala.",
    cta_emotional: "Inbox je nepriateľ. Tvoj agent je ochrana.",
    demo_url: null,
  },
  {
    slug: "contract-risk",
    name: "Contract Risk Scanner",
    tagline: "PDF zmluva → per-clause risk score + redline návrh za 90 sekúnd.",
    emoji: "⚖️",
    accent: "#EF4444",
    price_eur_month: 129,
    coming_soon: true,
    eta: "Q4 2026",
    saves: {
      time_hours_per_week: 3,
      money_eur_per_month: 1800,
      energy: "Žiadne viac 'právnik tvrdil že je to OK'.",
    },
    does_for_you: [
      "Per-clause risk score (low / medium / high / critical)",
      "Redline návrhy s SK/EN právnym jazykom",
      "Detekuje toxické klauzuly (auto-renewal, IP transfer, non-compete)",
      "Audit log pre GDPR Article 28 DPA",
    ],
    vs_manual: {
      manual: "Právnik €150/h × 2h/zmluva = €300",
      with_agent: "€129/mes pre unlimited zmlúv",
      saves_pct: 93,
    },
    pain_quote: "Jedna toxická klauzula ti môže stáť €50 000 pri exit-e. Vieš o nej?",
    cta_emotional: "Podpíš s pokojom. Alebo radšej nepodpíš.",
    demo_url: null,
  },
  {
    slug: "lead-qualifier",
    name: "Lead Qualifier Agent",
    tagline: "Forms, emaily, LinkedIn DMs → BANT score + auto-push do CRM.",
    emoji: "🎣",
    accent: "#10B981",
    price_eur_month: 99,
    coming_soon: true,
    eta: "Q4 2026",
    saves: {
      time_hours_per_week: 8,
      money_eur_per_month: 1500,
      energy: "Žiadny viac stratený čas na tire-kickerov.",
    },
    does_for_you: [
      "BANT scoring (Budget/Authority/Need/Timeline)",
      "Enrichuje z LinkedIn + company data",
      "Auto-push do HubSpot / Pipedrive / Salesforce",
      "Návrh prvej odpovede v tvojom voice",
    ],
    vs_manual: {
      manual: "SDR €1800/mes + 50% lead-ov rozhodí",
      with_agent: "€99/mes + 0% lead leak",
      saves_pct: 94,
    },
    pain_quote: "Z 100 leadov je 12 reálnych. Vieš ktorých 12, alebo voláš všetkým?",
    cta_emotional: "Volaj len tým, čo kupujú. Ostatok dostane email.",
    demo_url: null,
  },
  {
    slug: "accounting",
    name: "Accounting Agent",
    tagline: "Mesačná uzávierka za 2 hodiny namiesto 2 dní. Bez chýb.",
    emoji: "📊",
    accent: "#F59E0B",
    price_eur_month: 149,
    coming_soon: true,
    eta: "Q3 2026",
    saves: {
      time_hours_per_week: 6,
      money_eur_per_month: 2400,
      energy: "Žiadne nočné mory pri DPH priznaní.",
    },
    does_for_you: [
      "Auto-zaúčtovanie bank transactions",
      "Sync s Pohoda / iDoklad / Money S3",
      "Mesačná uzávierka + cash-flow forecast",
      "DPH priznanie pre-fill",
    ],
    vs_manual: {
      manual: "Účtovník €450/mes part-time",
      with_agent: "€149/mes + real-time dáta",
      saves_pct: 67,
    },
    pain_quote: "Účtovník ti pošle bilanciu 25. v mesiaci. Konkurencia ju má 1. v mesiaci.",
    cta_emotional: "Vedz svoje čísla, kým konkurencia ešte hľadá svoje.",
    demo_url: null,
  },
  {
    slug: "inventory-pulse",
    name: "Inventory Pulse Agent",
    tagline: "Predpovedá out-of-stock 14 dní dopredu. Žiadny viac 'meškáme s objednávkou'.",
    emoji: "📦",
    accent: "#06B6D4",
    price_eur_month: 99,
    coming_soon: true,
    eta: "Q4 2026",
    saves: {
      time_hours_per_week: 4,
      money_eur_per_month: 2200,
      energy: "Žiadne viac panika pri Black Friday.",
    },
    does_for_you: [
      "AI demand forecast per SKU (14-day window)",
      "Auto-alert pri reorder pointe",
      "Optimal order quantity (EOQ formula)",
      "Slow-mover destocking suggestions",
    ],
    vs_manual: {
      manual: "Out-of-stock = priemerne 8% obratu mesačne stratené",
      with_agent: "OOS rate < 1.5%",
      saves_pct: 81,
    },
    pain_quote: "5 najlepších SKU ti zarobí 60% obratu. A presne tieto ti chýbajú v sklade.",
    cta_emotional: "Sklad, ktorý vie, čo bude predávať. Skôr ako ty.",
    demo_url: null,
  },
  {
    slug: "returns-triage",
    name: "Returns Triage Agent",
    tagline: "Reklamácia príde → agent rozhodne refund/replace/escalate za 30 sekúnd.",
    emoji: "↩️",
    accent: "#EF4444",
    price_eur_month: 79,
    coming_soon: true,
    eta: "Q4 2026",
    saves: {
      time_hours_per_week: 5,
      money_eur_per_month: 900,
      energy: "Žiadne viac 'čo s tým robí' otázky.",
    },
    does_for_you: [
      "Klasifikuje return reason (damaged / wrong / regret / fraud)",
      "Návrh rozhodnutia (full refund / partial / replacement / deny)",
      "Auto-email odpoveď v tvojom tone",
      "Fraud pattern detection (serial returners)",
    ],
    vs_manual: {
      manual: "CS agent rozhoduje 20min × 30 returns/mes = 10h",
      with_agent: "Auto-decision + ty schvális za 1min",
      saves_pct: 89,
    },
    pain_quote: "8% objednávok sa vráti. To je €4 800 mesačne (pri €60k obrate) v limbu.",
    cta_emotional: "Reklamácie nezabíjajú biznis. Pomalé reklamácie áno.",
    demo_url: null,
  },
  {
    slug: "local-seo-pages",
    name: "Local SEO Pages Agent",
    tagline: "Vygeneruje 26 mestských landing pages cez noc. Bratislava, Praha, Wien, Berlin...",
    emoji: "🗺️",
    accent: "#10B981",
    price_eur_month: 99,
    coming_soon: true,
    eta: "Q3 2026",
    saves: {
      time_hours_per_week: 10,
      money_eur_per_month: 1800,
      energy: "Žiadne viac 'mali by sme mať podstránky pre mestá'.",
    },
    does_for_you: [
      "Per-city landing page (26 EU lokácií)",
      "Hyperlokálny copy (lokálne referencie, mestské insights)",
      "Schema.org LocalBusiness markup",
      "Auto-sitemap + Google indexing ping",
    ],
    vs_manual: {
      manual: "Copywriter €2.10 × 26 stránok = €54, + dev čas",
      with_agent: "€99/mes pre unlimited + auto-publish",
      saves_pct: 88,
    },
    pain_quote: "Konkurencia má '15 najlepších kvetinárstiev v Bratislave'. Ty nemáš ani jednu.",
    cta_emotional: "26 miest, 26 príležitostí. Tento týždeň.",
    demo_url: null,
  },
  {
    slug: "shipping-optimizer",
    name: "Shipping Optimizer Agent",
    tagline: "Vyberá najlacnejšieho prepravcu per objednávku. Šetrí 12-18% na poštovnom.",
    emoji: "🚚",
    accent: "#3B82F6",
    price_eur_month: 89,
    coming_soon: true,
    eta: "Q4 2026",
    saves: {
      time_hours_per_week: 2,
      money_eur_per_month: 1400,
      energy: "Žiadne viac 'GLS alebo Packeta?' rozmýšľanie.",
    },
    does_for_you: [
      "Real-time rate compare (GLS, Packeta, DHL, DPD, GoPost)",
      "Auto-pick podľa rozmer/váha/destinácia/SLA",
      "Tracking page v tvojom brand",
      "SLA monitoring + claim assistant",
    ],
    vs_manual: {
      manual: "Jednu prepravnú zmluvu na všetko = +14% navyše",
      with_agent: "Per-order best rate",
      saves_pct: 14,
    },
    pain_quote: "Pri €60k obrate platíš €8400 ročne na poštovnom navyše. Bez optimizéra.",
    cta_emotional: "Marža je v detailoch. Aj v poštovnom.",
    demo_url: null,
  },
  {
    slug: "tax-vat",
    name: "Tax & VAT Agent",
    tagline: "Cross-border DPH, OSS, IOSS — agent vie kde, koľko a kedy zaplatiť.",
    emoji: "🏛️",
    accent: "#8B5CF6",
    price_eur_month: 119,
    coming_soon: true,
    eta: "Q1 2027",
    saves: {
      time_hours_per_week: 3,
      money_eur_per_month: 1100,
      energy: "Žiadne viac stresu z DPH kontroly.",
    },
    does_for_you: [
      "Auto-DPH za EU (OSS / IOSS)",
      "Threshold tracking per krajinu",
      "Pre-fill VAT priznanie",
      "Audit-ready transaction log",
    ],
    vs_manual: {
      manual: "Daňový poradca €200/h × 6h kvartál = €4800/rok",
      with_agent: "€119/mes = €1428/rok",
      saves_pct: 70,
    },
    pain_quote: "Predáš do Nemecka 9 999 €. Hraničná hodnota €10 000. Vedel si že už registračná povinnosť?",
    cta_emotional: "Daniari neodpúšťajú. Tvoj agent áno.",
    demo_url: null,
  },
  {
    slug: "chargeback-defender",
    name: "Chargeback Defender Agent",
    tagline: "Stripe/PayPal dispute → agent zhromaždí dôkazy a napíše rebuttal. Win rate 73%.",
    emoji: "🛡️",
    accent: "#F59E0B",
    price_eur_month: 79,
    coming_soon: true,
    eta: "Q1 2027",
    saves: {
      time_hours_per_week: 2,
      money_eur_per_month: 1600,
      energy: "Žiadne viac 'už zase chargeback'.",
    },
    does_for_you: [
      "Auto-collect evidence (delivery proof, IP logs, tracking)",
      "Generuje rebuttal letter (Stripe-friendly format)",
      "Submit do Stripe Dispute API / PayPal Center",
      "Win rate analytics + pattern insights",
    ],
    vs_manual: {
      manual: "30% win rate pri DIY rebutalu",
      with_agent: "73% win rate (priemer pilot klienti)",
      saves_pct: 143,  // win-rate uplift
    },
    pain_quote: "Pri €60k obrate ti chargebacky ukrojia €1800-3000 mesačne. Nepotrebuje to byť tak.",
    cta_emotional: "Bojuj o svoje peniaze. Nech to robí agent.",
    demo_url: null,
  },

  // ─────────────────────────────────────────────────────────────
  // V144 — Ďalšie 8 agentov ("pripravujeme" wave 2)
  // Pokrývajú HR / Photography / Influencer / Marketplaces / Cash
  // Flow / Localization / Founder-OS / Loyalty pain points pre EU
  // SMB e-shop owners.
  // ─────────────────────────────────────────────────────────────
  {
    slug: "hr-recruiter",
    name: "HR Recruiter Agent",
    tagline: "Napíše inzerát, pošle na Profesiu + LinkedIn + FB groups, screenuje CVčka. Nábor za 7 dní namiesto 47.",
    emoji: "👥",
    accent: "#3B82F6",
    price_eur_month: 99,
    coming_soon: true,
    eta: "Q3 2026",
    saves: {
      time_hours_per_week: 6,
      money_eur_per_month: 1800,
      energy: "Žiadne viac '4 týždne hľadám packera'.",
    },
    does_for_you: [
      "Generuje inzerát v brand voice (3 platformy súčasne)",
      "Push na Profesia.sk / LinkedIn / FB Jobs / Indeed",
      "AI screening CV (score 0-100 podľa job requirements)",
      "Auto-pozvanky na pohovor pre top 5",
    ],
    vs_manual: {
      manual: "Recruiter €1800/mes + 47-dňový time-to-hire",
      with_agent: "€99/mes + 7-dňový shortlist",
      saves_pct: 95,
    },
    pain_quote: "Chýba ti packer. Strácaš €450/deň na zlých dodaniach. A nábor trvá 47 dní.",
    cta_emotional: "Hire-fast alebo lose-fast. Iný spôsob neexistuje.",
    demo_url: null,
  },
  {
    slug: "product-photo-ai",
    name: "Product Photo AI Agent",
    tagline: "1 nudná fotka produktu → 6 lifestyle variant + background removal + 4K upscale za 30 sekúnd.",
    emoji: "📸",
    accent: "#EC4899",
    price_eur_month: 79,
    coming_soon: true,
    eta: "Q3 2026",
    saves: {
      time_hours_per_week: 5,
      money_eur_per_month: 1200,
      energy: "Žiadne viac fotograf €500/deň, žiadne Photoshop.",
    },
    does_for_you: [
      "AI background removal (white / transparent / scene)",
      "Generuje lifestyle scény (kúpeľňa, kuchyňa, outdoor, atď.)",
      "4K upscale + denoise pre print materials",
      "Bulk export pre Heureka / Google Shopping (správne rozmery)",
    ],
    vs_manual: {
      manual: "Fotograf €500/deň + retoucher 2h/produkt × €25",
      with_agent: "€79/mes pre unlimited produktov",
      saves_pct: 94,
    },
    pain_quote: "Konkurencia má lifestyle fotky. Ty máš 'white background'. Konverzia hovorí ostatok.",
    cta_emotional: "Pekné fotky predávajú. Stretchnúť rozpočet sa neoplatí.",
    demo_url: null,
  },
  {
    slug: "influencer-outreach",
    name: "Influencer Outreach Agent",
    tagline: "Nájde micro-influencerov (1k-50k) v tvojej niche, napíše DM, trackuje response rate. Bez agentúry.",
    emoji: "🎤",
    accent: "#8B5CF6",
    price_eur_month: 129,
    coming_soon: true,
    eta: "Q4 2026",
    saves: {
      time_hours_per_week: 8,
      money_eur_per_month: 3500,
      energy: "Žiadne viac influencer agency €3500/mes.",
    },
    does_for_you: [
      "Scrape IG/TikTok podľa niche + engagement (>3%)",
      "Personalizovaný DM v ich štýle",
      "Track open / reply / collab rate",
      "Návrh barter / paid deal podľa ich tier",
    ],
    vs_manual: {
      manual: "Influencer agency €3500/mes + 6-týždňový sprint",
      with_agent: "€129/mes + 100 outreach/týž",
      saves_pct: 96,
    },
    pain_quote: "Konkurencia má 12 micro-influencerov. Ty máš 0. To je 240 000 impressions/mes navyše pre nich.",
    cta_emotional: "Mikro-influenceri predávajú. Tvoja konkurencia to už pochopila.",
    demo_url: null,
  },
  {
    slug: "marketplaces-sync",
    name: "Marketplaces Sync Agent",
    tagline: "1 feed → Heureka Marketplace + Allegro + Amazon + eBay + Wolt. Bez per-platform admin.",
    emoji: "🌐",
    accent: "#10B981",
    price_eur_month: 149,
    coming_soon: true,
    eta: "Q4 2026",
    saves: {
      time_hours_per_week: 12,
      money_eur_per_month: 5000,
      energy: "Žiadne viac 5× admin panel pre 5 marketplaces.",
    },
    does_for_you: [
      "Cross-post produktov na 5+ marketplaces",
      "Auto-pricing per marketplace (fees + margin)",
      "Centralizovaný inventory sync (žiadne over-sell)",
      "Order forwarding do tvojho fulfillmentu",
    ],
    vs_manual: {
      manual: "5× channel manager €1000/mes + ručná synchronizácia",
      with_agent: "€149/mes + 1 central feed",
      saves_pct: 97,
    },
    pain_quote: "10 000 ľudí denne hľadá tvoj produkt na Allegre / Amazone. Ty tam nie si.",
    cta_emotional: "Buď tam, kde sú zákazníci. Vo všetkých 5 kanáloch.",
    demo_url: null,
  },
  {
    slug: "cash-flow-forecast",
    name: "Cash Flow Forecast Agent",
    tagline: "Pozrie bank + faktúry + recurring → predpovedá 90-dňový runway. Vie kedy ti dôjdu peniaze.",
    emoji: "💵",
    accent: "#F59E0B",
    price_eur_month: 99,
    coming_soon: true,
    eta: "Q4 2026",
    saves: {
      time_hours_per_week: 3,
      money_eur_per_month: 800,
      energy: "Žiadne nočné mory typu 'budem mať na výplatu?'",
    },
    does_for_you: [
      "Bank API integrácia (Tatra / Slovenská sporiteľna / VÚB / Erste / Komerční / SaltEdge)",
      "Detekuje recurring expenses + výnosy",
      "90-day forecast s 3 scenármi (worst / base / best)",
      "Alert pri runway < 60 dní",
    ],
    vs_manual: {
      manual: "Excel + accountant 2h/týž = €200 + slepá viera",
      with_agent: "€99/mes + real-time alerts",
      saves_pct: 75,
    },
    pain_quote: "85% SMB firiem skrachuje na cash flow, nie na profite. Vidíš svoj 90-dňový horizont?",
    cta_emotional: "Predvídaj. Alebo platíš pokuty za omeškanie.",
    demo_url: null,
  },
  {
    slug: "localization",
    name: "Localization Agent",
    tagline: "Preloží celý shop (UI + emaily + legal + produkt popisy) do novej EU krajiny za 24h.",
    emoji: "🌍",
    accent: "#06B6D4",
    price_eur_month: 169,
    coming_soon: true,
    eta: "Q1 2027",
    saves: {
      time_hours_per_week: 0,   // one-time setup
      money_eur_per_month: 4500,
      energy: "Žiadne 6-mesačné expansion plánovanie.",
    },
    does_for_you: [
      "Translate shop (UI labels + product titles + descriptions)",
      "Localize email templates (transakčné + marketing)",
      "Generate legal pages (Terms / Privacy / GDPR per krajinu)",
      "Currency / tax / shipping rules pre novú krajinu",
    ],
    vs_manual: {
      manual: "Translator agency €4500 one-time + 6 týždňov",
      with_agent: "€169/mes pre unlimited countries",
      saves_pct: 95,
    },
    pain_quote: "Predáš v 1 krajine. Konkurencia v 7. Ich CAC je 4× nižší vďaka diverzifikácii.",
    cta_emotional: "Expanduj. Pred tým, ako ťa expanduje konkurencia.",
    demo_url: null,
  },
  {
    slug: "founder-os",
    name: "Founder OS · Daily Brief Agent",
    tagline: "Ranný 3-priorities brief + week-in-review. CEO coach vo vrecku za €79/mes namiesto €4000/mes.",
    emoji: "🧭",
    accent: "#EF4444",
    price_eur_month: 79,
    coming_soon: true,
    eta: "Q3 2026",
    saves: {
      time_hours_per_week: 4,
      money_eur_per_month: 600,
      energy: "Strategický pokoj namiesto firefightingu.",
    },
    does_for_you: [
      "Ranný 06:30 brief: 3 najdôležitejšie veci dnes (priority-sorted)",
      "Eveníng wrap-up: čo si zvládol/nezvládol + reflection prompt",
      "Týždenný recap (KPI + insights + na ďalší týždeň)",
      "Mesačná retro + OKR check-in",
    ],
    vs_manual: {
      manual: "Executive coach €4000/mes + 1× týždenne 1h call",
      with_agent: "€79/mes + denný rhythm",
      saves_pct: 98,
    },
    pain_quote: "Začneš deň reaktívne. Skončíš ho vyčerpane. Strategický focus = stratený.",
    cta_emotional: "Predtým CEO. Teraz s CEO operating system.",
    demo_url: null,
  },
  {
    slug: "loyalty-winback",
    name: "Loyalty & Win-back Agent",
    tagline: "Segmentuje dormant customers (60+ dní bez nákupu), posiela cielenú win-back. Reaktivácia 18-22%.",
    emoji: "💝",
    accent: "#10B981",
    price_eur_month: 89,
    coming_soon: true,
    eta: "Q4 2026",
    saves: {
      time_hours_per_week: 3,
      money_eur_per_month: 2400,
      energy: "Existujúci zákazník je 5× lacnejší než nový.",
    },
    does_for_you: [
      "Segmenty: VIP / regular / churned / never-bought-again",
      "Cielená 3-stage win-back sekvencia per segment",
      "Personalizovaná zľava (smart discount podľa LTV)",
      "Loyalty tier system (Bronze / Silver / Gold)",
    ],
    vs_manual: {
      manual: "Generic 'we miss you' email + 10% off = 4% reaktivácia",
      with_agent: "Personalizované sekvencie = 18-22% reaktivácia",
      saves_pct: 450,  // 4× lift
    },
    pain_quote: "Stojí ťa €120 získať nového. Reaktivovať starého stojí €18. Ktorých robíš?",
    cta_emotional: "Vráť starých zákazníkov. Skôr, než to spravia konkurenti.",
    demo_url: null,
  },
];

export function liveAgents() {
  return AGENTS.filter((a) => !a.coming_soon);
}

export function comingSoonAgents() {
  return AGENTS.filter((a) => a.coming_soon);
}

export function findAgent(slug) {
  return AGENTS.find((a) => a.slug === slug);
}
